/* Size chart modal (Kiwi Sizing replacement) — the cm/in toggle only.
 *
 * The tables themselves are server-rendered by snippets/size-chart-table.liquid
 * from the sizing_chart metaobject; this file converts the numbers in place and
 * restates each one's unit after it.
 *
 * Per Table grammar v1 (docs/size-chart-brief.md) a measurement cell is one of
 * exactly two shapes — a bare number (17.2) or a hyphen range (71-75). Every
 * other cell (size names, notes, values that bake in their own units) is left
 * exactly as authored.
 *
 * Two cells are excluded outright, whatever their text:
 *   - the header row, which holds column labels
 *   - the FIRST cell of every body row, which holds the size label. Across the
 *     harvest's 326 body rows that cell is never a real measurement, but 55 of
 *     them carry a bare number there — a jean size 36, say — which the textual
 *     rule would otherwise corrupt into 14. Pinned in the brief 2026-08-12.
 *
 * Each cell's authored text is kept in data-source-value, and every conversion
 * runs from that value rather than from what is on screen, so toggling back and
 * forth can never accumulate rounding drift.
 */
(() => {
  const BARE_NUMBER = /^\d+(?:\.\d+)?$/;
  const HYPHEN_RANGE = /^(\d+(?:\.\d+)?)\s*-\s*(\d+(?:\.\d+)?)$/;

  // Source ranges are sloppy (they overlap), so the conversions round coarse:
  // nearest half-inch one way, whole centimetres the other.
  const toInches = (cm) => Math.round((cm / 2.54) * 2) / 2;
  const toCentimetres = (inches) => Math.round(inches * 2.54);

  // Ranges are stored canonically (81-86) and shown spaced (81 – 86). The same
  // rule is applied server-side by snippets/size-chart-row.liquid for the first
  // render; the two must stay in step. Storage never carries the spacing.
  const RANGE_SEPARATOR = ' – ';

  // Every measurement cell states its own unit after the value — "81 – 86 cm",
  // "32 – 34″" — so a number read out of the middle of a wide chart carries its
  // unit with it instead of depending on the caption above the table. A thin
  // space before cm; nothing before the double prime, which hugs its number the
  // way primes do. Characters rather than CSS margins, so a copied cell reads
  // the same as the rendered one. size-chart-row.liquid holds the same two
  // strings for the first render. Storage never carries the suffix.
  const UNIT_SUFFIX = { cm: ' cm', in: '″' };
  const UNIT_SUFFIX_CLASS = 'size-chart__unit-suffix';

  // The prime takes a modifier because it must not be shrunk with the rest of
  // the suffix — see THE UNIT SUFFIX in size-chart.css.
  const PRIME_SUFFIX_CLASS = 'size-chart__unit-suffix--prime';

  /**
   * Converts one cell's authored text into the requested unit.
   * @param {string} text - Canonical cell text.
   * @param {string} from - The chart's native unit ('cm' or 'in').
   * @param {string} to - Requested unit ('cm' or 'in').
   * @returns {string} Canonical converted text, or the original when it isn't a measurement.
   */
  function convert(text, from, to) {
    if (from === to) return text;

    const convertOne = to === 'in' ? toInches : toCentimetres;

    if (BARE_NUMBER.test(text)) return String(convertOne(parseFloat(text)));

    const range = text.match(HYPHEN_RANGE);
    if (range) return `${convertOne(parseFloat(range[1]))}-${convertOne(parseFloat(range[2]))}`;

    return text;
  }

  /**
   * Applies the display rule to a canonical value.
   * @param {string} text - Canonical cell text.
   * @returns {string} Text as it should be shown.
   */
  function formatForDisplay(text) {
    const range = text.match(HYPHEN_RANGE);
    if (range) return `${range[1]}${RANGE_SEPARATOR}${range[2]}`;

    return text;
  }

  /**
   * The unit suffix one canonical value should carry.
   *
   * The same two cell shapes the converter recognises, and only those: a cell
   * holding anything else says whatever it says, and appending a unit to it
   * would be this file speaking over the author.
   * @param {string} text - Canonical cell text.
   * @param {string} unit - The unit on show ('cm', 'in', or blank).
   * @returns {string} The suffix, or '' when the cell isn't a measurement.
   */
  function suffixFor(text, unit) {
    if (!BARE_NUMBER.test(text) && !HYPHEN_RANGE.test(text)) return '';

    return UNIT_SUFFIX[unit] || '';
  }

  /**
   * Writes one canonical value into a cell, formatted and suffixed — the whole
   * display rule in one place, so the toggle can never move the value without
   * moving its unit with it.
   *
   * The suffix is a child span rather than the cell's own ::after, because the
   * fit overlay's pointer already owns ::before and ::after on a lit cell.
   * Writing the text first also clears any band markers the overlay left
   * behind; syncFitOverlay repaints them straight after.
   * @param {Element} cell - A tbody td.
   * @param {string} text - Canonical cell text, already converted.
   * @param {string} unit - The unit on show ('cm' or 'in').
   */
  function writeCell(cell, text, unit) {
    cell.textContent = formatForDisplay(text);

    const suffix = suffixFor(text, unit);
    if (suffix === '') return;

    const span = cell.ownerDocument.createElement('span');

    span.className = unit === 'in' ? `${UNIT_SUFFIX_CLASS} ${PRIME_SUFFIX_CLASS}` : UNIT_SUFFIX_CLASS;
    span.textContent = suffix;
    cell.appendChild(span);
  }

  /**
   * Switches one chart's table to the given unit.
   * @param {Element} chart - The .size-chart element.
   * @param {string} unit - Requested unit ('cm' or 'in').
   */
  function setUnit(chart, unit) {
    const nativeUnit = chart.dataset.nativeUnit;
    if (unit !== 'cm' && unit !== 'in') return;

    chart.querySelectorAll('.size-chart__table tbody td:not(:first-child)').forEach((cell) => {
      // Measurement cells arrive with their canonical value already on the
      // element, because what they show is spaced, suffixed or both. Every
      // other cell shows its canonical text, so reading it back off the
      // element is the same value.
      if (cell.dataset.sourceValue === undefined) {
        cell.dataset.sourceValue = cell.textContent.trim();
      }
      writeCell(cell, convert(cell.dataset.sourceValue, nativeUnit, unit), unit);
    });

    chart.querySelectorAll('.size-chart__unit').forEach((btn) => {
      btn.setAttribute('aria-pressed', btn.dataset.unit === unit ? 'true' : 'false');
    });

    // The caption is the only place the unit is spelled out, so it has to
    // follow the toggle. Both wordings come from the markup, not from here.
    const caption = chart.querySelector('.size-chart__caption');
    if (caption) {
      const wording = unit === 'in' ? caption.dataset.captionIn : caption.dataset.captionCm;
      if (wording) caption.textContent = wording;
    }

    syncFitOverlay(chart, unit); // FIT OVERLAY (prototype) — goes with the block below.
  }

  /**
   * The unit a chart is currently showing.
   * @param {Element} chart - The .size-chart element.
   * @returns {string} 'cm' or 'in'.
   */
  function displayedUnit(chart) {
    const pressed = chart.querySelector('.size-chart__unit[aria-pressed="true"]');
    return pressed ? pressed.dataset.unit : chart.dataset.nativeUnit;
  }

  // Delegated so the toggle survives a section re-render (variant change) with
  // no re-initialisation.
  document.addEventListener('click', (evt) => {
    const button = evt.target.closest('.size-chart__unit');
    if (!button) return;

    const chart = button.closest('.size-chart');
    if (!chart) return;

    const unit = button.dataset.unit;
    if (unit !== 'cm' && unit !== 'in') return;

    // The choice is remembered and applied to every chart in the modal, not
    // only the one clicked — see THE REMEMBERED UNIT at the foot of this file.
    rememberUnit(unit);
    setUnitEverywhere(button.closest('.size-chart-modal') || document, unit);
  });

  /* ======================================================================
   * ADVANCED COLUMNS (grammar v1.1, 2026-08-13)
   *
   * A `+` on a header cell marks a column as advanced; size-chart-table.liquid
   * strips the marker, tags the column's cells and renders the disclosure.
   * All this does is flip the table's state and keep the two things that
   * depend on how many columns are showing in step: the fixed layout's
   * column count, and the fit overlay's bold row labels.
   *
   * A chart with no advanced columns has no control and never reaches here.
   * ===================================================================== */

  /**
   * Shows or hides one table's advanced columns.
   * @param {Element} table - The .size-chart__table element.
   * @param {Element} button - Its .size-chart__more control.
   * @param {boolean} show - Whether the advanced columns should be on show.
   */
  function setAdvancedColumns(table, button, show) {
    table.dataset.advancedHidden = show ? 'false' : 'true';
    button.setAttribute('aria-expanded', show ? 'true' : 'false');

    // Both wordings come from the markup, the same way the unit caption's do,
    // so there is one place to translate them.
    const label = show ? button.dataset.labelLess : button.dataset.labelMore;
    if (label) button.textContent = label;

    // The fixed layout is sized to what is on screen — see --size-chart-cols
    // in size-chart.css. Revealing columns is an explicit action, so the
    // table reshaping around them is the point rather than a violation of
    // the no-reshape rule.
    const columns = parseInt(table.dataset.columnCount, 10);
    const advanced = parseInt(table.dataset.advancedCount, 10);
    if (columns > 0) {
      table.style.setProperty('--size-chart-cols', show ? columns : columns - advanced);
    }

    // A row bolded by a cell that just went out of view would be a claim with
    // nothing behind it, so the labels are worked out again either way.
    const chart = table.closest('.size-chart');
    if (chart) markMatchedRows(chart);
  }

  document.addEventListener('click', (evt) => {
    const button = evt.target.closest('.size-chart__more');
    if (!button) return;

    const chart = button.closest('.size-chart');
    const table = chart && chart.querySelector('.size-chart__table');
    if (!table) return;

    setAdvancedColumns(table, button, button.getAttribute('aria-expanded') !== 'true');
  });

  /* ======================================================================
   * FIT OVERLAY (prototype, 2026-08-12)
   *
   * Optional inputs in the table head, one per measurement column. Each one
   * that has a value lights EVERY body cell whose range contains it, and
   * marks where inside each range it falls. Reading three columns at once is
   * the point: "all mid-Medium" and "high-M, high-M, low-L" are different
   * answers, and the customer draws both of them without the page saying
   * anything. No verdict, no recommended size, no scoring.
   *
   * An input is read by the count of numbers in it: one is a point, two are
   * a band. The two are answering different questions, so they are drawn
   * differently —
   *
   *   POINT (7)   this is my measurement. Containment plus a pointer at the
   *               place inside each lit cell where the value falls. Flat
   *               tint: a point has no width, so nothing may draw one.
   *   BAND (6-8)  I am somewhere in here, or I want everything that fits a
   *               span. Every row the band overlaps takes its share: the
   *               covered part of the cell fills solid, the rest stays
   *               faint, and each END of the band gets the same pointer a
   *               single value would. Read down the column that makes one
   *               shape. Fill means coverage, pointers mean position.
   *
   * Nothing typed, or three or more numbers, lights nothing, silently.
   *
   * Both work off SPANS, and a column of bare single numbers is turned into
   * spans before either of them runs — see territoriesFor. So there is one
   * engine, not a range one and a single-value one.
   *
   * Self-contained: delete this block and the frozen-panes one that follows
   * it, the fit row in snippets/size-chart-table.liquid, the fit section of
   * size-chart.css and the syncFitOverlay call in setUnit, and the chart is
   * exactly as it was.
   * ===================================================================== */

  const FIT_CLASS = 'size-chart__cell--fit';
  const FIT_POINT_CLASS = 'size-chart__cell--fit-point';
  const FIT_BAND_CLASS = 'size-chart__cell--fit-band';
  const FIT_MARKER_CLASS = 'size-chart__fit-marker';
  const FIT_LABEL_CLASS = 'size-chart__label--fit';
  const FIT_BELOW_CLASS = 'size-chart__cell--fit-below';
  const FIT_ABOVE_CLASS = 'size-chart__cell--fit-above';
  const ADVANCED_CLASS = 'size-chart__cell--advanced';

  // A fit input is read by pulling the NUMBERS out of it and ignoring
  // whatever sits between them. One number is a point, two are a band, and
  // anything else lights nothing. So "6-8", "6 - 8", "6–8", "6,8", "6 to 8"
  // and "6 8" all ask the same question, and a range copied straight out of
  // a cell (which shows as "81 – 86") works without the customer tidying it.
  // The table grammar is untouched by this — cells are still stored and
  // parsed as a bare number or a plain-hyphen range. Nothing in these charts
  // is negative, so a hyphen is only ever a separator.
  const NUMBER_TOKEN = /\d+(?:\.\d+)?/g;

  /**
   * Parses a body cell into the span of bodies it covers.
   * @param {Element} cell - A tbody td.
   * @returns {?{low: number, high: number}} null when the cell isn't a measurement.
   */
  function spanOf(cell) {
    // Measurement cells carry their canonical value (what they show is spaced,
    // suffixed or both); everything else shows it.
    const text = cell.dataset.sourceValue !== undefined ? cell.dataset.sourceValue : cell.textContent.trim();

    const range = text.match(HYPHEN_RANGE);
    if (range) return { low: parseFloat(range[1]), high: parseFloat(range[2]) };

    if (BARE_NUMBER.test(text)) {
      const point = parseFloat(text);
      return { low: point, high: point };
    }

    return null;
  }

  /**
   * The point halfway between two chart values, to the precision a chart
   * value is ever written at.
   *
   * The rounding is not fussiness. Halfway between 1.4 and 1.7 comes out of
   * binary floating point as 1.5499999999999998, and a customer who works
   * out the boundary and types 1.55 would land just past it and light one
   * row where the seam should light two. Across the 57 live charts, 42 of
   * the 544 boundaries land on dust like that, so the seam would fire on
   * some boundaries and not others for no reason anyone could see. Chart
   * values carry two decimals, three at the very most (3.14, 9.87), so
   * their midpoints carry at most four; six decimal places is far below
   * anything the data means and far above the noise.
   * @param {number} a - One value.
   * @param {number} b - The next value.
   * @returns {number} The midpoint.
   */
  function midpoint(a, b) {
    return Math.round(((a + b) / 2) * 1e6) / 1e6;
  }

  /**
   * The spans one column is actually read against.
   *
   * A column holding any authored range is taken exactly as written — the
   * chart author said where each size starts and stops, and nothing here
   * second-guesses that.
   *
   * A column of bare single numbers says nothing about where one size stops
   * and the next begins, so the overlay INFERS it: each value owns the
   * territory running to the halfway point with the value either side of it.
   * The Grip's Neck Diameter goes … 2, 2.4 …, so 2 owns up to 2.2 and 2.4
   * owns from 2.2. That turns a column of points into a column of ranges,
   * and everything downstream — containment, the pointer, band overlap — is
   * the range machinery it already had, with no special case for single
   * values anywhere below this function.
   *
   * The outermost values are NOT extended outwards: the first value's
   * territory starts at the value itself and the last one's ends at it, so
   * the column covers exactly what it covered before and the off-scale rule
   * is untouched.
   *
   * Midpoints are taken between adjacent values in SORTED order, not in row
   * order. Every live chart happens to run smallest to largest, but a chart
   * that did not would otherwise produce territories that overlap and cross.
   * Equal values collapse to one territory, so a plateau (Insertable Length
   * ends 8, 8, 8) lights as one answer spread over three rows.
   *
   * @param {Array<{cell: Element, span: {low: number, high: number}}>} measurable - The column's measurement cells.
   * @returns {Array<{cell: Element, span: {low: number, high: number}}>} The same cells against the spans to compare.
   */
  function territoriesFor(measurable) {
    if (measurable.some(({ span }) => span.high !== span.low)) return measurable;

    const values = Array.from(new Set(measurable.map(({ span }) => span.low))).sort((a, b) => a - b);
    const territory = new Map();

    values.forEach((value, index) => {
      territory.set(value, {
        low: index === 0 ? value : midpoint(values[index - 1], value),
        high: index === values.length - 1 ? value : midpoint(value, values[index + 1]),
      });
    });

    return measurable.map(({ cell, span }) => ({ cell, span: territory.get(span.low) }));
  }

  /**
   * Lights one cell. The tint is binary — this range contains you — and the
   * position places the pointer within it, like a mark on a ruler. See the
   * fit-overlay section of size-chart.css.
   * @param {Element} cell - The cell to light.
   * @param {number} position - 0 at the bottom of the range, 1 at the top.
   */
  function lightCell(cell, position) {
    cell.classList.add(FIT_CLASS, FIT_POINT_CLASS);
    cell.style.setProperty('--fit-position', `${(position * 100).toFixed(1)}%`);
  }

  /**
   * Puts a pointer at one place inside a cell.
   *
   * Point mode hangs its single pointer off the cell's own ::before and
   * ::after. A band can want TWO in one cell — when it starts and stops
   * inside the same size — and an element has only the two pseudo-elements,
   * so band pointers are real child spans instead. They are empty, so the
   * cell's text is unaffected, and they carry the same two pieces (hairline,
   * caret) from the same declarations in size-chart.css.
   * @param {Element} cell - The cell to mark.
   * @param {number} position - 0 at the bottom of the cell's span, 1 at the top.
   */
  function addMarker(cell, position) {
    const marker = cell.ownerDocument.createElement('span');

    marker.className = FIT_MARKER_CLASS;
    marker.style.setProperty('--fit-at', `${(position * 100).toFixed(1)}%`);
    cell.appendChild(marker);
  }

  /**
   * Draws one cell's share of a band.
   *
   * The cell is filled solid across the part of it the band covers and left
   * faint elsewhere, and each END of the band that lands inside this cell
   * gets a pointer. That one rule covers every case: a row in the middle of
   * the band is covered end to end and comes out solid with no pointer; the
   * first and last rows are part solid, part faint, with one pointer each;
   * and a band living inside a single size is faint, solid, faint with a
   * pointer at both ends.
   *
   * The fill here IS a quantity — how much of this size the band covers —
   * which is exactly what §35 said a fill must never be for a single value.
   * Both hold: for a point there is no width to draw, so a fill would be
   * invention; for a band the width is the thing the customer typed.
   *
   * @param {Element} cell - The cell to light.
   * @param {{low: number, high: number}} span - The cell's own range or territory.
   * @param {{low: number, high: number}} band - The query, in canonical units.
   */
  function paintBandCell(cell, span, band) {
    const width = span.high - span.low;

    // A cell with no width is either covered or it is not; there is no
    // fraction of it to draw, and nowhere inside it to point at.
    const from = width === 0 ? 0 : Math.min(1, Math.max(0, (band.low - span.low) / width));
    const to = width === 0 ? 1 : Math.min(1, Math.max(0, (band.high - span.low) / width));

    cell.classList.add(FIT_CLASS, FIT_BAND_CLASS);
    cell.style.setProperty('--fit-from', `${(from * 100).toFixed(1)}%`);
    cell.style.setProperty('--fit-to', `${(to * 100).toFixed(1)}%`);

    if (width === 0) return;

    if (band.low >= span.low && band.low <= span.high) addMarker(cell, from);
    if (band.high >= span.low && band.high <= span.high) addMarker(cell, to);
  }

  /**
   * Bolds the size label of every row holding a lit cell, so the answer reads
   * straight down the first column instead of having to be assembled across
   * the table. Runs after every column is painted.
   *
   * A lit cell in a COLLAPSED advanced column does not count. The bold label
   * is a summary of what is on screen — "these are your sizes, read down" —
   * and a row bolded by a cell nobody can see is a claim with no evidence
   * behind it. The typed value is kept and the cell stays painted, so
   * revealing the column brings the bolding with it; this runs again on
   * every toggle.
   * @param {Element} chart - The .size-chart element.
   */
  function markMatchedRows(chart) {
    const table = chart.querySelector('.size-chart__table');
    if (!table) return;

    const advancedHidden = table.dataset.advancedHidden === 'true';
    const litSelector = advancedHidden
      ? `.${FIT_CLASS}:not(.${ADVANCED_CLASS})`
      : `.${FIT_CLASS}`;

    table.querySelectorAll('tbody tr').forEach((row) => {
      const label = row.cells[0];
      if (!label) return;

      label.classList.toggle(FIT_LABEL_CLASS, row.querySelector(litSelector) !== null);
    });
  }

  /**
   * Repaints one column of the overlay.
   * @param {Element} chart - The .size-chart element.
   * @param {number} columnIndex - Column to paint; column 0 is the size label and never participates.
   * @param {?{low: number, high: number, band: boolean}} query - Canonical (native-unit) query, or null to clear the column.
   */
  function paintColumn(chart, columnIndex, query) {
    const measurable = [];

    chart.querySelectorAll('.size-chart__table tbody tr').forEach((row) => {
      const cell = row.cells[columnIndex];
      if (!cell) return;

      cell.classList.remove(FIT_CLASS, FIT_POINT_CLASS, FIT_BAND_CLASS, FIT_BELOW_CLASS, FIT_ABOVE_CLASS);
      cell.style.removeProperty('--fit-position');
      cell.style.removeProperty('--fit-from');
      cell.style.removeProperty('--fit-to');
      cell.querySelectorAll(`.${FIT_MARKER_CLASS}`).forEach((marker) => marker.remove());

      const span = spanOf(cell);
      if (span) measurable.push({ cell, span });
    });

    if (query === null || measurable.length === 0) return;

    // Authored ranges as written; a column of single values as the
    // territories inferred from them. One kind of thing from here down.
    const comparable = territoriesFor(measurable);

    // What the column itself covers, from the AUTHORED values — see the
    // off-scale note further down for why it is never the territories.
    const columnLow = measurable.reduce((low, { span }) => Math.min(low, span.low), Infinity);
    const columnHigh = measurable.reduce((high, { span }) => Math.max(high, span.high), -Infinity);
    const firstCell = measurable[0].cell;
    const lastCell = measurable[measurable.length - 1].cell;

    // BAND: every row the band overlaps takes its share of it, drawn as the
    // covered part of that row filling solid — so the band reads as one
    // shape running down the column rather than as a set of matches.
    if (query.band) {
      comparable
        .filter(({ span }) => span.high >= query.low && span.low <= query.high)
        .forEach(({ cell, span }) => paintBandCell(cell, span, query));

      // A band running past the end of the chart says so at that end, with
      // the same edge line an off-scale point draws. The rows it does cover
      // still fill to the physical end of the column.
      if (query.low < columnLow) firstCell.classList.add(FIT_BELOW_CLASS);
      if (query.high > columnHigh) lastCell.classList.add(FIT_ABOVE_CLASS);
      return;
    }

    // POINT: a single number, placed against the column.
    const value = query.low;

    // EVERY range that contains the value lights, not just the first one
    // found. Some charts are being rewritten at port time to deliberately
    // overlapping size profiles (XS 81-86 alongside S 84-89), where fitting
    // two sizes IS the answer — the tight one and the loose one — and picking
    // one silently would throw away the point of the chart. On charts whose
    // bounds merely touch (81-86 then 86-92) an exact seam value lights both,
    // each with its pointer at its own end of its own range, which is the
    // honest reading of a seam.
    //
    // Inferred territories touch by construction, so the same rule gives a
    // single-value column the same honest seam: on The Grip's Neck Diameter,
    // 2.2 is the boundary between 2's territory and 2.4's, and it lights
    // both rows with the pointers at the facing edges.
    const containing = comparable.filter(({ span }) => value >= span.low && value <= span.high);
    if (containing.length > 0) {
      containing.forEach(({ cell, span }) => {
        lightCell(cell, span.high === span.low ? 0.5 : (value - span.low) / (span.high - span.low));
      });
      return;
    }

    // OFF THE END OF THE COLUMN, either kind of column. Nothing matched, so
    // the value is either past one of the ends or in a gap between two
    // authored ranges. Past an end, nothing lights and the end it ran past
    // gets an edge line — "past this", without inventing an off-scale
    // interface. In a bounded gap, nothing at all: the chart genuinely does
    // not cover that measurement, and that is not an edge. Territories tile
    // their column with no gaps, so only a range column reaches that case.
    //
    // Measured against the AUTHORED values, deliberately, not the
    // territories: inferring where sizes meet must never widen what the
    // chart claims to cover. It does not today — the outermost territories
    // stop at their own values — and reading the authored spans here keeps
    // that true no matter what the model above grows into.
    //
    // WHETHER the value is off the end is a question about values; WHERE the
    // line is drawn is a question about the table. The line goes on the
    // column's first or last row, full stop. It used to go on whichever row
    // held the extreme value, which put it mid-table whenever values
    // plateau: The Grip's Insertable column ends 8, 8, 8 on its last three
    // rows, so an above-scale value drew the line under the FIRST of them
    // and read as though the two rows below it were not there. Ties,
    // plateaus and rows that are not in ascending order all stop mattering
    // once the anchor is positional.
    if (value < columnLow) firstCell.classList.add(FIT_BELOW_CLASS);
    else if (value > columnHigh) lastCell.classList.add(FIT_ABOVE_CLASS);
  }

  /**
   * Stores what was typed as a canonical, native-unit value — a bare number
   * for a point, a hyphen range for a band, the same two shapes the cells
   * use. The overlay only ever reads that, so flipping the toggle relabels
   * the boxes without moving a marker: the numbers in the box change, the
   * body or object they describe does not. A band converts end by end, under
   * the same rounding as the cells, so the ends stay in step with the column
   * they are compared against.
   * @param {Element} input - A .size-chart__fit-input.
   * @param {Element} chart - The .size-chart element.
   */
  function readFitInput(input, chart) {
    const numbers = input.value.match(NUMBER_TOKEN);
    const displayed = displayedUnit(chart);

    if (numbers !== null && numbers.length === 1) {
      input.dataset.canonicalValue = convert(numbers[0], displayed, chart.dataset.nativeUnit);
      return;
    }

    if (numbers !== null && numbers.length === 2) {
      // Typed back to front (8-6) is still a legible span, so it is read as
      // one rather than thrown away.
      const ends = numbers.map((end) => parseFloat(end)).sort((a, b) => a - b);
      input.dataset.canonicalValue = convert(`${ends[0]}-${ends[1]}`, displayed, chart.dataset.nativeUnit);
      return;
    }

    // Nothing typed yet, or three or more numbers, which is not a question
    // this row can answer. Silent, exactly as an empty box is.
    delete input.dataset.canonicalValue;
  }

  /**
   * Reads a stored canonical value back into a query for the painter.
   * @param {string|undefined} stored - A .size-chart__fit-input's canonical value.
   * @returns {?{low: number, high: number, band: boolean}} null when the box holds nothing usable.
   */
  function queryOf(stored) {
    if (stored === undefined) return null;

    const band = stored.match(HYPHEN_RANGE);
    if (band) return { low: parseFloat(band[1]), high: parseFloat(band[2]), band: true };

    const point = parseFloat(stored);
    return { low: point, high: point, band: false };
  }

  /**
   * Repaints every column of one chart from its inputs.
   * @param {Element} chart - The .size-chart element.
   */
  function paintFit(chart) {
    chart.querySelectorAll('.size-chart__fit-input').forEach((input) => {
      paintColumn(chart, parseInt(input.dataset.fitCol, 10), queryOf(input.dataset.canonicalValue));
    });

    markMatchedRows(chart);
  }

  /**
   * Keeps the overlay in step with the unit toggle.
   * @param {Element} chart - The .size-chart element.
   * @param {string} unit - The unit now on show.
   */
  function syncFitOverlay(chart, unit) {
    chart.querySelectorAll('.size-chart__fit-input').forEach((input) => {
      const stored = input.dataset.canonicalValue;
      if (stored === undefined) return;

      input.value = convert(stored, chart.dataset.nativeUnit, unit);
    });

    paintFit(chart);
  }

  document.addEventListener('input', (evt) => {
    const input = evt.target.closest('.size-chart__fit-input');
    if (!input) return;

    const chart = input.closest('.size-chart');
    if (!chart) return;

    readFitInput(input, chart);
    paintFit(chart);
  });

  /* ======================================================================
   * FROZEN PANES — the FIND row's top offset
   *
   * The header row and the size column are pinned in CSS alone (see the
   * FROZEN PANES section of size-chart.css); their offsets are both zero, so
   * there is nothing to work out. The FIND row is the one exception: it
   * freezes directly BELOW the header row, so its offset is that row's
   * rendered height, and headers wrap — "Chest (at armhole seam to seam)"
   * takes one line or three depending on how wide the modal is and how many
   * columns are on show. So it is measured and published as
   * --size-chart-head-top on the table.
   *
   * A ResizeObserver on the header row answers every trigger with one
   * mechanism: the viewport resizing, the "More measurements" disclosure
   * rewrapping the labels, the brand font arriving late, and the modal being
   * opened at all. Nothing has to hook Modal.open(), and the row can be
   * measured before it is ever seen — the theme hides a closed modal with
   * `visibility: hidden`, which still lays its contents out.
   *
   * Goes with the fit overlay above: if that is ripped out there is no FIND
   * row to place and this block goes with it.
   * ===================================================================== */

  const HEAD_TOP_PROPERTY = '--size-chart-head-top';

  const headRowObserver =
    'ResizeObserver' in window
      ? new ResizeObserver((entries) => {
          entries.forEach((entry) => {
            const table = entry.target.closest('.size-chart__table');
            if (!table) return;

            // borderBoxSize rather than a bounding rect, because a closed
            // modal window is scaled to 0.8 and a rect would be scaled with
            // it; the layout size is what the sticky offset is in.
            const box = entry.borderBoxSize && entry.borderBoxSize[0];
            const height = box ? box.blockSize : entry.target.offsetHeight;

            // A row with no height has not been laid out yet. Leaving the
            // property unset is the safe state — the FIND row scrolls with
            // the body — where a 0 offset would freeze it over the header.
            if (height <= 0) return;

            // Floored, so that a fractional row height leaves the FIND row a
            // hair UNDER the header rather than a hairline of body text
            // showing between the two. The header row paints above it.
            table.style.setProperty(HEAD_TOP_PROPERTY, `${Math.floor(height)}px`);
          });
        })
      : null;

  /**
   * Starts measuring the header row of every chart table that has a FIND row
   * to place under it. Observing the same row twice does nothing, so this is
   * safe to run again whenever the tables might have been replaced.
   */
  function watchHeaderRows() {
    if (!headRowObserver) return;

    document.querySelectorAll('.size-chart__table').forEach((table) => {
      const headRow = table.querySelector('thead tr:first-child');
      if (headRow && table.querySelector('.size-chart__fit-row')) headRowObserver.observe(headRow);
    });
  }

  watchHeaderRows();

  // The product section re-renders on a variant change, which swaps in table
  // elements the observer has never seen. Re-scanning as the modal is opened
  // catches them; the rows are laid out by then either way, so the offset is
  // set before anything is on screen.
  document.addEventListener('click', (evt) => {
    if (evt.target.closest('.size-chart-link')) watchHeaderRows();
  });

  /* ======================================================================
   * THE REMEMBERED UNIT
   *
   * A chart renders in its own native unit, which is whatever unit its author
   * happened to work in. A customer who reads in inches was re-selecting
   * INCHES on every product, and on the 14 products carrying more than one
   * chart the modal could open with one table in cm and the next in inches,
   * because the two charts were authored in different units.
   *
   * So the choice is remembered — one key, the unit, nothing else. It is
   * applied through setUnit, the same path a click takes, so the cells, the
   * suffixes, the caption, the FIND values and the fit overlay all follow with
   * no second conversion rule to keep in step. With nothing stored, every
   * chart still opens in its native unit exactly as before.
   *
   * There is no control for forgetting it: picking the other unit IS the
   * update, and a "reset my preference" button would be a third thing to
   * explain in a modal that is meant to answer one question.
   *
   * This is the one thing the storefront remembers about a customer's reading
   * of a chart, and it is remembered because it is a fact about them — which
   * units they think in. How the chart is STYLED is editorial and is not
   * theirs to keep (the show-the-units toggle was proposed and rejected on
   * exactly that line).
   *
   * Every localStorage call is guarded. Safari in private browsing throws on
   * write, and a browser with storage disabled throws on read; either way the
   * page behaves as it did before this block existed — the toggle still works,
   * the choice simply is not carried to the next product.
   * ===================================================================== */

  const UNIT_STORAGE_KEY = 'size-chart-unit';

  /**
   * The unit the customer last chose.
   * @returns {?string} 'cm', 'in', or null when nothing usable is stored.
   */
  function rememberedUnit() {
    let stored = null;

    try {
      stored = window.localStorage.getItem(UNIT_STORAGE_KEY);
    } catch (error) {
      return null;
    }

    return stored === 'cm' || stored === 'in' ? stored : null;
  }

  /**
   * Records the customer's choice. A write that fails costs nothing else: the
   * chart in front of them has already switched.
   * @param {string} unit - 'cm' or 'in'.
   */
  function rememberUnit(unit) {
    try {
      window.localStorage.setItem(UNIT_STORAGE_KEY, unit);
    } catch (error) {
      /* Private browsing, or a full quota. Nothing to fall back to. */
    }
  }

  /**
   * Puts every chart under a root into one unit.
   *
   * Charts with no toggle are left alone: their `unit` is empty because their
   * cells bake their units into the text, and there is nothing to convert.
   * Charts already showing the unit are skipped, so nothing is repainted for
   * no reason — and on the first pass that is most of them.
   * @param {Element|Document} root - Where to look for charts.
   * @param {string} unit - 'cm' or 'in'.
   */
  function setUnitEverywhere(root, unit) {
    root.querySelectorAll('.size-chart').forEach((chart) => {
      if (!chart.querySelector('.size-chart__unit')) return;
      if (displayedUnit(chart) === unit) return;

      setUnit(chart, unit);
    });
  }

  /**
   * Applies the remembered choice to whatever is on the page now.
   *
   * Runs once at load — the modal's markup is server-rendered and laid out
   * behind `visibility: hidden`, so the tables are converted before they are
   * ever seen — and again as the modal is opened, which is what catches the
   * fresh markup a variant change swaps in.
   */
  function applyRememberedUnit() {
    const unit = rememberedUnit();
    if (unit === null) return;

    setUnitEverywhere(document, unit);
  }

  applyRememberedUnit();

  document.addEventListener('click', (evt) => {
    if (evt.target.closest('.size-chart-link')) applyRememberedUnit();
  });
})();
